import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api/api';
import { Department } from '../../types';
import './HallsPayrollPage.css';

type EmployeePayroll = {
  appUserId: string;
  employeeName: string;
  workedHours: number;
  evaluationCoefficient: number;
  allocationWeight: number;
  amount: number;
  adjustments?: Array<{ amount: number; reason: string; createdAtUtc: string; createdByUsername: string }>;
};

type UnmatchedVenue = { venueName: string; orderCount: number; totalPrice: number };
type VenueAlias = { id: number; eventVenueName: string; eventVenueId: number; venueName: string; isActive: boolean };
type VenueOption = { id: number; name: string };
type PayrollPeriod = {
  exists: boolean;
  id?: number;
  year: number;
  month: number;
  departmentId?: number;
  departmentNameSnapshot?: string;
  additionalServicesRevenue?: number;
  additionalServicesDirectCosts?: number;
  additionalServicesProfit?: number;
  orderRevenue?: number;
  orderRevenueSnapshot?: number;
  orderRevenuePoolSnapshot?: number;
  orderRevenuePool?: number;
  additionalServicesPool?: number;
  additionalServicesProfitSnapshot?: number;
  additionalServicesPoolSnapshot?: number;
  orderRevenuePercent?: number;
  additionalServicesProfitPercent?: number;
  orderRevenuePercentSnapshot?: number;
  additionalServicesProfitPercentSnapshot?: number;
  totalPool?: number;
  totalPoolSnapshot?: number;
  status?: 'draft' | 'approved' | 'closed';
  version: number;
  notes?: string;
  employees?: EmployeePayroll[];
  mappedOrders?: Array<{ id: number; eventDate: string; sourceVenueName: string; mappedVenueName: string; totalPrice: number }>;
  unmatchedOrders?: UnmatchedVenue[];
  approvedAtUtc?: string;
  approvedByUsername?: string;
  closedAtUtc?: string;
  closedByUsername?: string;
};

type AliasResponse = { aliases: VenueAlias[]; unmatchedNames: UnmatchedVenue[] };

const currency = new Intl.NumberFormat('uk-UA', { style: 'currency', currency: 'UAH' });
const localMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const createIdempotencyKey = () => (
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`
);

const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } }).response;
    if (response?.data?.message) return response.data.message;
  }
  return fallback;
};

const HallsPayrollPage: React.FC = () => {
  const [periodInput, setPeriodInput] = useState(localMonth);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [period, setPeriod] = useState<PayrollPeriod | null>(null);
  const [aliases, setAliases] = useState<VenueAlias[]>([]);
  const [unmatchedNames, setUnmatchedNames] = useState<UnmatchedVenue[]>([]);
  const [venues, setVenues] = useState<VenueOption[]>([]);
  const [additionalRevenue, setAdditionalRevenue] = useState('0');
  const [directCosts, setDirectCosts] = useState('0');
  const [notes, setNotes] = useState('');
  const [orderRevenuePercent, setOrderRevenuePercent] = useState('12');
  const [additionalServicesProfitPercent, setAdditionalServicesProfitPercent] = useState('50');
  const [departmentId, setDepartmentId] = useState('');
  const [aliasName, setAliasName] = useState('');
  const [aliasVenueId, setAliasVenueId] = useState('');
  const [adjustmentEmployeeId, setAdjustmentEmployeeId] = useState('');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjustmentIdempotencyKey, setAdjustmentIdempotencyKey] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [yearText, monthText] = periodInput.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const hallsDepartments = useMemo(
    () => departments.filter(department => department.name.trim() === 'Весільні Зали'),
    [departments],
  );
  const isDraft = !period?.exists || period.status === 'draft';
  const displayOrderRevenue = period?.orderRevenue ?? period?.orderRevenueSnapshot ?? 0;
  const displayProfit = period?.additionalServicesProfit ?? period?.additionalServicesProfitSnapshot ?? 0;
  const displayTotal = period?.totalPool ?? period?.totalPoolSnapshot ?? 0;
  const employees = period?.employees ?? [];

  const loadPeriod = useCallback(async (active: { current: boolean }) => {
    if (!year || month < 1 || month > 12) return;
    setBusy(true);
    setError('');
    setNotice('');
    setPeriod(null);
    try {
      const [periodResponse, departmentsResponse, aliasesResponse, venuesResponse, accessResponse] = await Promise.all([
        api.get<PayrollPeriod>('/halls-payroll', { params: { year, month } }),
        api.get<Department[]>('/departments'),
        api.get<AliasResponse>('/halls-payroll/venue-aliases'),
        api.get<VenueOption[]>('/halls-payroll/venue-options'),
        api.get<{ editPermissions: string[] }>('/users/me/access'),
      ]);
      if (!active.current) return;
      const current = periodResponse.data;
      setDepartments(departmentsResponse.data);
      setPeriod(current);
      setOrderRevenuePercent(String(current.orderRevenuePercent ?? current.orderRevenuePercentSnapshot ?? 12));
      setAdditionalServicesProfitPercent(String(current.additionalServicesProfitPercent ?? current.additionalServicesProfitPercentSnapshot ?? 50));
      setAliases(aliasesResponse.data.aliases);
      setUnmatchedNames(aliasesResponse.data.unmatchedNames);
      setVenues(venuesResponse.data);
      setCanEdit(accessResponse.data.editPermissions.includes('page.management.halls-payroll'));
      if (current.exists) {
        setDepartmentId(String(current.departmentId ?? ''));
        setAdditionalRevenue(String(current.additionalServicesRevenue ?? 0));
        setDirectCosts(String(current.additionalServicesDirectCosts ?? 0));
        setNotes(current.notes ?? '');
      } else {
        const halls = departmentsResponse.data.filter(department => department.name.trim() === 'Весільні Зали');
        setDepartmentId(halls.length === 1 ? String(halls[0].id) : '');
        setAdditionalRevenue('0');
        setDirectCosts('0');
        setNotes('');
      }
      setAdjustmentEmployeeId(current.employees?.[0]?.appUserId ?? '');
      setAliasName(aliasesResponse.data.unmatchedNames[0]?.venueName ?? '');
      setAliasVenueId(venuesResponse.data[0]?.id ? String(venuesResponse.data[0].id) : '');
    } catch (loadError) {
      if (active.current) setError(getErrorMessage(loadError, 'Не вдалося завантажити зарплатний період.'));
    } finally {
      if (active.current) setBusy(false);
    }
  }, [year, month]);

  useEffect(() => {
    const active = { current: true };
    void loadPeriod(active);
    return () => { active.current = false; };
  }, [loadPeriod]);

  const reload = async () => {
    const active = { current: true };
    await loadPeriod(active);
  };

  const saveRates = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.put('/halls-payroll/rates', {
        effectiveYear: year,
        effectiveMonth: month,
        orderRevenuePercent: Number(orderRevenuePercent),
        additionalServicesProfitPercent: Number(additionalServicesProfitPercent),
      });
      await reload();
      setNotice(`Відсотки нарахування діють з ${String(month).padStart(2, '0')}.${year}; погоджені знімки не змінюються.`);
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'Не вдалося зберегти відсотки фонду.'));
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    if (!departmentId || hallsDepartments.every(department => department.id !== Number(departmentId))) {
      setError('Оберіть єдиний підрозділ «Весільні Зали».');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await api.put<PayrollPeriod>(`/halls-payroll/${year}/${month}`, {
        version: period?.exists ? period.version : 0,
        departmentId: Number(departmentId),
        additionalServicesRevenue: Number(additionalRevenue),
        additionalServicesDirectCosts: Number(directCosts),
        notes,
      });
      setPeriod(response.data);
      setNotice('Чернетку збережено. Нарахування ще не погоджене.');
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'Не вдалося зберегти чернетку.'));
    } finally {
      setBusy(false);
    }
  };

  const transition = async (action: 'approve' | 'close') => {
    if (!period?.exists) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await api.post<PayrollPeriod>(`/halls-payroll/${year}/${month}/${action}`, { version: period.version });
      setPeriod(response.data);
      setNotice(action === 'approve' ? 'Нарахування погоджено та зафіксовано.' : 'Період закрито. Подальші зміни лише окремими коригуваннями.');
    } catch (transitionError) {
      setError(getErrorMessage(transitionError, 'Не вдалося змінити статус періоду.'));
    } finally {
      setBusy(false);
    }
  };

  const addAlias = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!aliasName.trim() || !aliasVenueId) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.post('/halls-payroll/venue-aliases', {
        eventVenueName: aliasName,
        eventVenueId: Number(aliasVenueId),
      });
      await reload();
      setNotice('Назву замовлення явно зіставлено із залом.');
    } catch (aliasError) {
      setError(getErrorMessage(aliasError, 'Не вдалося зберегти зіставлення залу.'));
    } finally {
      setBusy(false);
    }
  };

  const disableAlias = async (id: number) => {
    setBusy(true);
    setError('');
    try {
      await api.delete(`/halls-payroll/venue-aliases/${id}`);
      await reload();
      setNotice('Зіставлення вимкнено; замовлення з цією назвою виключені з розрахунку.');
    } catch (aliasError) {
      setError(getErrorMessage(aliasError, 'Не вдалося вимкнути зіставлення.'));
    } finally {
      setBusy(false);
    }
  };

  const addAdjustment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!period?.exists || !adjustmentEmployeeId || !adjustmentAmount || !adjustmentReason.trim()) return;
    setBusy(true);
    setError('');
    const idempotencyKey = adjustmentIdempotencyKey || createIdempotencyKey();
    setAdjustmentIdempotencyKey(idempotencyKey);
    try {
      await api.post(`/halls-payroll/${year}/${month}/adjustments`, {
        appUserId: adjustmentEmployeeId,
        amount: Number(adjustmentAmount),
        reason: adjustmentReason,
      }, {
        headers: { 'Idempotency-Key': idempotencyKey },
      });
      await reload();
      setAdjustmentAmount('');
      setAdjustmentReason('');
      setAdjustmentIdempotencyKey('');
      setNotice('Коригування додано окремим записом; закритий розрахунок не змінено.');
    } catch (adjustmentError) {
      setError(getErrorMessage(adjustmentError, 'Не вдалося додати коригування.'));
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = period?.status === 'closed' ? 'Закрито' : period?.status === 'approved' ? 'Погоджено' : 'Чернетка';

  return (
    <main className="halls-payroll-page">
      <header className="halls-payroll-header">
        <div>
          <h1>Зарплата — Весільні Зали</h1>
          <p>Розрахунок за завершеними замовленнями та ручними даними додаткових послуг.</p>
        </div>
        <label className="halls-payroll-month">
          Період
          <input aria-label="Період" type="month" value={periodInput} onChange={event => setPeriodInput(event.target.value)} />
        </label>
      </header>

      {error && <div className="halls-payroll-alert halls-payroll-alert--error" role="alert">{error}</div>}
      {notice && <div className="halls-payroll-alert halls-payroll-alert--success" role="status">{notice}</div>}
      {!canEdit && <div className="halls-payroll-alert">Перегляд доступний, але для внесення змін потрібне право редагування «Зарплата весільних залів».</div>}
      {busy && <div className="halls-payroll-loading" role="status">Завантаження…</div>}

      <section className="halls-payroll-card">
        <h2>Правила фонду підрозділу</h2>
        <p className="halls-payroll-help">Відсотки налаштовуються з обраного місяця. Для погоджених періодів формули та суми залишаються зафіксованими.</p>
        <div className="halls-payroll-input-grid">
          <label>Відсоток від виторгу замовлень
            <input aria-label="Відсоток від виторгу замовлень" type="number" min="0" max="100" step="0.01" value={orderRevenuePercent} onChange={event => setOrderRevenuePercent(event.target.value)} disabled={!canEdit || busy || !isDraft} />
          </label>
          <label>Відсоток від прибутку додаткових послуг
            <input aria-label="Відсоток від прибутку додаткових послуг" type="number" min="0" max="100" step="0.01" value={additionalServicesProfitPercent} onChange={event => setAdditionalServicesProfitPercent(event.target.value)} disabled={!canEdit || busy || !isDraft} />
          </label>
        </div>
        {canEdit && <button type="button" onClick={() => void saveRates()} disabled={busy || !isDraft} className="halls-payroll-button halls-payroll-button--primary">Зберегти правило з {String(month).padStart(2, '0')}.{year}</button>}
      </section>

      <section className="halls-payroll-summary" aria-label="Підсумок нарахування">
        <article><span>Виторг зіставлених завершених замовлень</span><strong>{currency.format(displayOrderRevenue)}</strong><small>{orderRevenuePercent}%: {currency.format(period?.orderRevenuePool ?? period?.orderRevenuePoolSnapshot ?? 0)}</small></article>
        <article><span>Прибуток додаткових послуг</span><strong>{currency.format(displayProfit)}</strong><small>{additionalServicesProfitPercent}%: {currency.format(period?.additionalServicesPool ?? period?.additionalServicesPoolSnapshot ?? 0)}</small></article>
        <article><span>Фонд розподілу</span><strong>{currency.format(displayTotal)}</strong><small>Статус: {statusLabel}</small></article>
      </section>

      <section className="halls-payroll-card">
        <h2>Дані місяця</h2>
        <div className="halls-payroll-input-grid">
          <label>HR-підрозділ
            <select value={departmentId} onChange={event => setDepartmentId(event.target.value)} disabled={!isDraft || !canEdit}>
              <option value="">Оберіть підрозділ</option>
              {hallsDepartments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
            </select>
          </label>
          <label>Виторг додаткових послуг, ₴
            <input type="number" min="0" step="0.01" inputMode="decimal" value={additionalRevenue} onChange={event => setAdditionalRevenue(event.target.value)} disabled={!isDraft || !canEdit} />
          </label>
          <label>Прямі витрати, ₴
            <input type="number" min="0" step="0.01" inputMode="decimal" value={directCosts} onChange={event => setDirectCosts(event.target.value)} disabled={!isDraft || !canEdit} />
          </label>
          <label className="halls-payroll-notes">Примітки
            <textarea value={notes} maxLength={2000} onChange={event => setNotes(event.target.value)} disabled={!isDraft || !canEdit} />
          </label>
        </div>
        {canEdit && isDraft && <button type="button" onClick={() => void saveDraft()} disabled={busy} className="halls-payroll-button halls-payroll-button--primary">Зберегти чернетку</button>}
        {canEdit && period?.status === 'draft' && (
          <button type="button" onClick={() => void transition('approve')} disabled={busy} className="halls-payroll-button halls-payroll-button--success">Погодити та зафіксувати розрахунок</button>
        )}
        {canEdit && period?.status === 'approved' && (
          <button type="button" onClick={() => void transition('close')} disabled={busy} className="halls-payroll-button halls-payroll-button--success">Закрити період</button>
        )}
        {period?.status === 'approved' && <p>Погодив(-ла): {period.approvedByUsername || '—'} · {period.approvedAtUtc ? new Date(period.approvedAtUtc).toLocaleString('uk-UA') : ''}</p>}
        {period?.status === 'closed' && <p>Закрив(-ла): {period.closedByUsername || '—'} · {period.closedAtUtc ? new Date(period.closedAtUtc).toLocaleString('uk-UA') : ''}</p>}
      </section>

      <section className="halls-payroll-card">
        <h2>Розподіл між працівниками</h2>
        <p className="halls-payroll-help">До розрахунку входять активні працівники цього підрозділу із завершеним табелем за місяць. Вага = відпрацьовані години × підсумковий коефіцієнт оцінювання.</p>
        <div className="halls-payroll-table-scroll">
          <table className="halls-payroll-table">
            <thead><tr><th>Працівник</th><th>Години</th><th>Коефіцієнт</th><th>Вага</th><th>Нараховано</th><th>Коригування</th></tr></thead>
            <tbody>
              {employees.map(employee => (
                <tr key={employee.appUserId}>
                  <td>{employee.employeeName}</td>
                  <td>{employee.workedHours.toFixed(2)}</td>
                  <td>{employee.evaluationCoefficient.toFixed(4)}</td>
                  <td>{employee.allocationWeight.toFixed(4)}</td>
                  <td>{currency.format(employee.amount)}</td>
                  <td>
                    {currency.format(employee.adjustments?.reduce((sum, adjustment) => sum + adjustment.amount, 0) ?? 0)}
                    {employee.adjustments?.map((adjustment, index) => (
                      <small key={`${adjustment.createdAtUtc}-${index}`} className="halls-payroll-adjustment-note">
                        {adjustment.reason} · {adjustment.createdByUsername} · {new Date(adjustment.createdAtUtc).toLocaleDateString('uk-UA')}
                      </small>
                    ))}
                  </td>
                </tr>
              ))}
              {!employees.length && <tr><td colSpan={6}>Немає працівників із зафіксованими годинами за цей період.</td></tr>}
            </tbody>
          </table>
        </div>
        {canEdit && period?.status === 'closed' && employees.length > 0 && (
          <form className="halls-payroll-adjustment" onSubmit={event => void addAdjustment(event)}>
            <h3>Окреме коригування закритого періоду</h3>
            <select aria-label="Працівник для коригування" value={adjustmentEmployeeId} onChange={event => { setAdjustmentEmployeeId(event.target.value); setAdjustmentIdempotencyKey(''); }}>
              {employees.map(employee => <option key={employee.appUserId} value={employee.appUserId}>{employee.employeeName}</option>)}
            </select>
            <input aria-label="Сума коригування" type="number" step="0.01" inputMode="decimal" value={adjustmentAmount} onChange={event => { setAdjustmentAmount(event.target.value); setAdjustmentIdempotencyKey(''); }} placeholder="Сума, ₴" />
            <input aria-label="Причина коригування" value={adjustmentReason} onChange={event => { setAdjustmentReason(event.target.value); setAdjustmentIdempotencyKey(''); }} maxLength={1000} placeholder="Причина" />
            <button className="halls-payroll-button halls-payroll-button--primary" disabled={busy}>Додати коригування</button>
          </form>
        )}
      </section>

      <section className="halls-payroll-card">
        <h2>Замовлення, включені в базу 12%</h2>
        <div className="halls-payroll-table-scroll">
          <table className="halls-payroll-table">
            <thead><tr><th>№</th><th>Дата події</th><th>Назва з замовлення</th><th>Зіставлений зал</th><th>Сума, ₴</th></tr></thead>
            <tbody>
              {period?.mappedOrders?.map(order => (
                <tr key={order.id}>
                  <td>{order.id}</td>
                  <td>{new Date(order.eventDate).toLocaleDateString('uk-UA')}</td>
                  <td>{order.sourceVenueName || '(порожня назва)'}</td>
                  <td>{order.mappedVenueName}</td>
                  <td>{currency.format(order.totalPrice)}</td>
                </tr>
              ))}
              {!period?.mappedOrders?.length && <tr><td colSpan={5}>У цьому періоді немає зіставлених завершених замовлень.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="halls-payroll-card">
        <h2>Зіставлення назв залів у замовленнях</h2>
        <p className="halls-payroll-help">Вільний текст із замовлення потрапляє у виторг лише після явного зіставлення із залом групи «Весільні Зали». Невідомі назви автоматично не включаються.</p>
        {canEdit && (
          <form className="halls-payroll-alias-form" onSubmit={event => void addAlias(event)}>
            <input aria-label="Назва залу в замовленні" list="halls-payroll-unmatched" value={aliasName} onChange={event => setAliasName(event.target.value)} placeholder="Назва залу в замовленні" maxLength={200} />
            <datalist id="halls-payroll-unmatched">{unmatchedNames.map(item => <option key={item.venueName} value={item.venueName} />)}</datalist>
            <select aria-label="Зал CRM" value={aliasVenueId} onChange={event => setAliasVenueId(event.target.value)}>
              {venues.map(venue => <option key={venue.id} value={venue.id}>{venue.name}</option>)}
            </select>
            <button className="halls-payroll-button halls-payroll-button--primary" disabled={busy || !venues.length}>Зіставити</button>
          </form>
        )}
        <div className="halls-payroll-table-scroll">
          <table className="halls-payroll-table">
            <thead><tr><th>Назва в замовленні</th><th>Зал CRM</th><th>Стан</th><th /></tr></thead>
            <tbody>
              {aliases.map(alias => (
                <tr key={alias.id}>
                  <td>{alias.eventVenueName}</td><td>{alias.venueName}</td><td>{alias.isActive ? 'Активне' : 'Вимкнене'}</td>
                  <td>{canEdit && alias.isActive && <button type="button" onClick={() => void disableAlias(alias.id)} disabled={busy} className="halls-payroll-button halls-payroll-button--danger">Вимкнути</button>}</td>
                </tr>
              ))}
              {!aliases.length && <tr><td colSpan={4}>Зіставлень ще немає.</td></tr>}
            </tbody>
          </table>
        </div>
        <h3>Незіставлені завершені замовлення</h3>
        <div className="halls-payroll-table-scroll">
          <table className="halls-payroll-table">
            <thead><tr><th>Назва з CRM</th><th>Кількість</th><th>Сума, не включена в розрахунок</th></tr></thead>
            <tbody>
              {unmatchedNames.map(item => <tr key={item.venueName}><td>{item.venueName || '(порожня назва)'}</td><td>{item.orderCount}</td><td>{currency.format(item.totalPrice)}</td></tr>)}
              {!unmatchedNames.length && <tr><td colSpan={3}>Незіставлених замовлень немає.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
};

export default HallsPayrollPage;
